/**
 * The notification center: what is waiting on platform staff, and what just
 * arrived.
 *
 * Built only from what the console already knows. "Waiting on you" is the
 * pending module-request count and the platform-health verdict the shell
 * already fetches; the list beneath is the newest organizations from the
 * overview endpoint, fetched when the panel is opened. There is no separate
 * notification store behind this, so nothing is marked read: it is a window
 * onto live state.
 */
import { Link } from 'react-router-dom';
import { Icon } from '../components/Icon.jsx';
import { Popover } from '../components/Popover.jsx';
import { StatusBadge } from '../components/Badge.jsx';
import { OrgAvatar } from '../components/Layout.jsx';
import { useApi } from '../hooks/useApi.js';
import * as adminApi from '../services/index.js';
import { number, relativeTime } from '../lib/format.js';

function NewestOrganizations() {
  const overview = useApi((signal) => adminApi.platformOverview({ signal }), []);
  const recent = overview.data?.recent ?? [];

  if (overview.loading) {
    return (
      <div className="notif-skeleton" aria-hidden="true">
        {[0, 1, 2].map((row) => (
          <div key={row} className="skeleton" style={{ width: `${82 - row * 12}%` }} />
        ))}
      </div>
    );
  }

  if (overview.error) {
    return (
      <div className="notif-empty">
        <p className="text-sm muted">The newest organizations could not be loaded.</p>
        <button type="button" className="btn btn-sm" onClick={overview.refetch}>
          Try again
        </button>
      </div>
    );
  }

  if (!recent.length) {
    return (
      <div className="notif-empty">
        <p className="text-sm muted">No organizations yet. The ones you create appear here.</p>
      </div>
    );
  }

  return (
    <div className="notif-feed">
      {recent.slice(0, 5).map((organization) => (
        <Link key={organization.id} to={`/organizations/${organization.id}`} className="notif-row notif-row-link">
          <OrgAvatar name={organization.name} size="sm" />
          <span className="notif-text">
            <span className="notif-title">{organization.name}</span>
            <span className="notif-meta">
              {organization.owner?.email ?? organization.slug} · {relativeTime(organization.createdAt)}
            </span>
          </span>
          <StatusBadge status={organization.status} />
        </Link>
      ))}
    </div>
  );
}

export function NotificationCenter({ moduleRequests = 0, health }) {
  const status = health?.status;
  const unhealthy = status === 'degraded' || status === 'unavailable';
  const waiting = moduleRequests > 0 || unhealthy;
  const affected =
    status === 'degraded' ? health?.degradedServices?.length ?? 0 : health?.unreachableServices?.length ?? 0;

  return (
    <Popover
      label="Notifications"
      align="end"
      panelClassName="popover-notifications"
      trigger={({ triggerProps, open }) => (
        <button
          type="button"
          className={`topbar-icon-btn ${open ? 'is-open' : ''}`}
          {...triggerProps}
          aria-label={waiting ? 'Notifications, something is waiting on you' : 'Notifications'}
          title="Notifications"
        >
          <Icon name="bell" size={17} />
          {waiting && <span className="topbar-dot" aria-hidden="true" />}
        </button>
      )}
    >
      <div className="notif">
        <div className="popover-title">Notifications</div>

        <div className="notif-section-label">Waiting on you</div>
        {moduleRequests > 0 && (
          <Link to="/module-requests" className="notif-attention">
            <span className="notif-attention-icon">
              <Icon name="module-request" size={16} />
            </span>
            <span className="grow">
              <span className="notif-title">
                {number(moduleRequests)} module {moduleRequests === 1 ? 'request is' : 'requests are'} waiting for a
                decision
              </span>
              <span className="notif-meta">Approve or reject</span>
            </span>
            <Icon name="chevron-right" size={15} className="subtle" />
          </Link>
        )}
        {unhealthy && (
          <Link to="/health" className="notif-attention notif-attention-danger">
            <span className="notif-attention-icon">
              <Icon name={status === 'unavailable' ? 'server-crash' : 'warning'} size={16} />
            </span>
            <span className="grow">
              <span className="notif-title">
                {status === 'unavailable'
                  ? `${number(affected)} ${affected === 1 ? 'service is' : 'services are'} not responding`
                  : `${number(affected)} ${affected === 1 ? 'service reports' : 'services report'} a problem`}
              </span>
              <span className="notif-meta">See service health</span>
            </span>
            <Icon name="chevron-right" size={15} className="subtle" />
          </Link>
        )}
        {!waiting && (
          <div className="notif-caught-up">
            <Icon name="success" size={16} />
            <span>You&rsquo;re all caught up.</span>
          </div>
        )}

        <div className="notif-section-label notif-section-row">
          Newest organizations
          <Link to="/organizations" className="popover-title-link">
            View all
          </Link>
        </div>
        <NewestOrganizations />
      </div>
    </Popover>
  );
}
